import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar } from '../../components/atoms';
import { Button } from '../../components/atoms';
import LogoSpinner from '../../components/atoms/LogoSpinner';
import { 
  AvailabilityDate, 
  AvailabilitySettings, 
  AvailabilityPageProps,
  createEmptyAvailabilityDate,
  createDefaultAvailabilitySettings,
  createDateFromString,
  getDateKey,
  datesEqual,
  normalizeAvailabilitySettings,
  applyBookingCountsToAvailability
} from '../../types';
import { 
  getAvailabilityRange,
  getAvailabilitySettings,
  updateAvailabilitySettings,
  updateAvailabilityDate,
  deleteAvailabilityDate,
  bulkUpdateAvailability,
  initializeDefaultAvailabilitySettings,
  upsertAvailabilityBookingCount
} from '../../services/firebase';
import { orderService } from '../../services/orderService';
import './AvailabilityPage.css';

const AvailabilityPage: React.FC<AvailabilityPageProps> = ({ className = '' }) => {
  const [availabilityDates, setAvailabilityDates] = useState<AvailabilityDate[]>([]);
  const [settings, setSettings] = useState<AvailabilitySettings | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showDayOfWeekSelector, setShowDayOfWeekSelector] = useState(false);
  const [showDateRangeSelector, setShowDateRangeSelector] = useState(false);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<number[]>([]);
  const [dateRangeFrom, setDateRangeFrom] = useState<string>('');
  const [dateRangeTo, setDateRangeTo] = useState<string>('');
  const fetchGenerationRef = useRef(0);

  // Fetch availability data for the current month
  const fetchAvailabilityData = useCallback(async (month: Date) => {
    const generation = ++fetchGenerationRef.current;
    try {
      setLoading(true);
      setError(null); // Clear any existing errors
      
      // Get first and last day of the month
      const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
      const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59, 999);
      
      // Fetch availability for the month
      const [availabilityData, settingsData, bookingCounts] = await Promise.all([
        getAvailabilityRange(firstDay, lastDay),
        getAvailabilitySettings(),
        orderService.getActiveBookingCountsByDate(firstDay, lastDay)
      ]);
      
      console.log('Fetched availability data for', month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), ':', availabilityData); // Debug log
      console.log('Blocked dates found:', availabilityData.filter(avail => avail.isBlocked)); // Debug log
      const mergedAvailability = applyBookingCountsToAvailability(availabilityData, bookingCounts);
      if (generation !== fetchGenerationRef.current) {
        return;
      }
      setAvailabilityDates(mergedAvailability);

      await Promise.all(
        mergedAvailability
          .filter((availability) => availability.currentBookings > 0)
          .map(async (availability) => {
            try {
              await upsertAvailabilityBookingCount(
                availability.date,
                availability.currentBookings
              );
            } catch (persistError) {
              console.error('Failed to persist booking count:', persistError);
            }
          })
      );
      
      // Initialize default settings if none exist
      if (generation !== fetchGenerationRef.current) {
        return;
      }

      if (!settingsData) {
        const defaultSettings = await initializeDefaultAvailabilitySettings();
        setSettings(normalizeAvailabilitySettings(defaultSettings));
      } else {
        setSettings(normalizeAvailabilitySettings(settingsData));
      }
      
      setLoading(false);
    } catch (err) {
      if (generation !== fetchGenerationRef.current) {
        return;
      }
      console.error('Error fetching availability data:', err);
      setError('Failed to load availability data. Please try again.');
      setLoading(false);
    }
  }, []);

  // Force refresh function that clears state and refetches
  const forceRefresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAvailabilityDates([]); // Clear existing data
      
      // Wait a moment to ensure state is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Refetch data
      await fetchAvailabilityData(currentMonth);
    } catch (err) {
      console.error('Error during force refresh:', err);
      setError('Failed to refresh data. Please try again.');
      setLoading(false);
    }
  }, [currentMonth, fetchAvailabilityData]);

  // Load data on component mount and when month changes
  useEffect(() => {
    fetchAvailabilityData(currentMonth);
  }, [currentMonth, fetchAvailabilityData]);

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedDates([date]);
  };

  // Handle date click (for toggling availability/blocking)
  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    // Toggle availability when clicking on a date
    toggleDateAvailability(date);
  };

  // Handle month change from calendar
  const handleMonthChange = (newDate: Date) => {
    setCurrentMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
  };

  // Toggle availability for a specific date (available by default, click to block/unblock)
  const toggleDateAvailability = async (date: Date) => {
    try {
      fetchGenerationRef.current += 1;
      setLoading(true);
      
      const existingAvailability = availabilityDates.find(avail => 
        datesEqual(avail.date, date)
      );
      
      if (existingAvailability) {
        if (existingAvailability.isBlocked) {
          // If currently blocked, remove the record (making it available by default)
          await deleteAvailabilityDate(date);
          
          // Remove from local state
          setAvailabilityDates(prev => 
            prev.filter(avail => avail.date.toDateString() !== date.toDateString())
          );
        } else {
          // If currently available, block it
          await updateAvailabilityDate(date, {
            isBlocked: true,
            isAvailable: false
          });
          
          // Update local state
          setAvailabilityDates(prev => 
            prev.map(avail => 
              datesEqual(avail.date, date)
                ? { ...avail, isBlocked: true, isAvailable: false }
                : avail
            )
          );
        }
      } else {
        // No record exists (available by default), create blocked record
        const newAvailability = createEmptyAvailabilityDate(date);
        newAvailability.isBlocked = true;
        newAvailability.isAvailable = false;
        
        await updateAvailabilityDate(date, newAvailability);
        
        // Add to local state
        setAvailabilityDates(prev => [...prev, newAvailability]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error toggling availability:', err);
      setError('Failed to update availability. Please try again.');
      setLoading(false);
    }
  };

  // Block/unblock all dates in current month
  const toggleAllDatesInMonth = async (block: boolean) => {
    try {
      setLoading(true);
      
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      const dates: Date[] = [];
      
      // Generate all dates in the current month
      const currentDate = new Date(startOfMonth);
      while (currentDate <= endOfMonth) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      if (block) {
        // Block all dates
        const newAvailabilityRecords = dates.map(date => ({
          ...createEmptyAvailabilityDate(date),
          isBlocked: true,
          isAvailable: false
        }));
        
        await bulkUpdateAvailability(dates, {
          isBlocked: true,
          isAvailable: false
        });
        
        // Update local state
        setAvailabilityDates(prev => {
          const filtered = prev.filter(avail => 
            !dates.some(date => datesEqual(date, avail.date))
          );
          return [...filtered, ...newAvailabilityRecords];
        });
      } else {
        // Unblock all dates (remove records)
        const datesToRemove = dates.map(date => getDateKey(date));
        
        // Remove from database
        for (const date of dates) {
          const existingAvailability = availabilityDates.find(avail => 
            datesEqual(avail.date, date)
          );
          if (existingAvailability) {
            await deleteAvailabilityDate(date);
          }
        }
        
        // Update local state
        setAvailabilityDates(prev => 
          prev.filter(avail => !datesToRemove.includes(getDateKey(avail.date)))
        );
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error toggling all dates:', err);
      setError('Failed to update availability. Please try again.');
      setLoading(false);
    }
  };

  // Block/unblock specific days of the week for next 6 months
  const toggleDaysOfWeek = async (block: boolean) => {
    try {
      setLoading(true);
      
      if (selectedDaysOfWeek.length === 0) {
        setError('Please select at least one day of the week.');
        setLoading(false);
        return;
      }
      
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0); // Reset to start of day
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 6);
      endDate.setHours(0, 0, 0, 0); // Reset to start of day
      
      const dates: Date[] = [];
      
      // Generate all dates for the next 6 months that match selected days of week
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        if (selectedDaysOfWeek.includes(currentDate.getDay())) {
          dates.push(new Date(currentDate));
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      if (block) {
        // Block selected days of week
        await bulkUpdateAvailability(dates, {
          isBlocked: true,
          isAvailable: false
        });
        
        // Update local state
        const newAvailabilityRecords = dates.map(date => ({
          ...createEmptyAvailabilityDate(date),
          isBlocked: true,
          isAvailable: false
        }));
        
        setAvailabilityDates(prev => {
          const filtered = prev.filter(avail => 
            !dates.some(date => datesEqual(date, avail.date))
          );
          return [...filtered, ...newAvailabilityRecords];
        });
      } else {
        // Unblock selected days of week (remove records)
        for (const date of dates) {
          const existingAvailability = availabilityDates.find(avail => 
            datesEqual(avail.date, date)
          );
          if (existingAvailability) {
            await deleteAvailabilityDate(date);
          }
        }
        
        // Update local state
        const datesToRemove = dates.map(date => getDateKey(date));
        setAvailabilityDates(prev => 
          prev.filter(avail => !datesToRemove.includes(getDateKey(avail.date)))
        );
      }
      
      setShowDayOfWeekSelector(false);
      setSelectedDaysOfWeek([]);
      setLoading(false);
    } catch (err) {
      console.error('Error toggling days of week:', err);
      setError('Failed to update availability. Please try again.');
      setLoading(false);
    }
  };

  // Block/unblock date range
  const toggleDateRange = async (block: boolean) => {
    try {
      setLoading(true);
      
      if (!dateRangeFrom || !dateRangeTo) {
        setError('Please select both start and end dates.');
        setLoading(false);
        return;
      }
      
      const startDate = createDateFromString(dateRangeFrom);
      const endDate = createDateFromString(dateRangeTo);
      
      if (startDate > endDate) {
        setError('Start date must be before end date.');
        setLoading(false);
        return;
      }
      
      const dates: Date[] = [];
      
      // Generate all dates in the range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      if (block) {
        // Block date range
        await bulkUpdateAvailability(dates, {
          isBlocked: true,
          isAvailable: false
        });
        
        // Update local state
        const newAvailabilityRecords = dates.map(date => ({
          ...createEmptyAvailabilityDate(date),
          isBlocked: true,
          isAvailable: false
        }));
        
        setAvailabilityDates(prev => {
          const filtered = prev.filter(avail => 
            !dates.some(date => datesEqual(date, avail.date))
          );
          return [...filtered, ...newAvailabilityRecords];
        });
      } else {
        // Unblock date range (remove records)
        for (const date of dates) {
          const existingAvailability = availabilityDates.find(avail => 
            datesEqual(avail.date, date)
          );
          if (existingAvailability) {
            await deleteAvailabilityDate(date);
          }
        }
        
        // Update local state
        const datesToRemove = dates.map(date => getDateKey(date));
        setAvailabilityDates(prev => 
          prev.filter(avail => !datesToRemove.includes(getDateKey(avail.date)))
        );
      }
      
      setShowDateRangeSelector(false);
      setDateRangeFrom('');
      setDateRangeTo('');
      setLoading(false);
    } catch (err) {
      console.error('Error toggling date range:', err);
      setError('Failed to update availability. Please try again.');
      setLoading(false);
    }
  };

  // Block/unblock a specific date
  const toggleDateBlocked = async (date: Date) => {
    try {
      setLoading(true);
      
      const existingAvailability = availabilityDates.find(avail => 
        datesEqual(avail.date, date)
      );
      
      if (existingAvailability) {
        // Update existing availability
        await updateAvailabilityDate(date, {
          isBlocked: !existingAvailability.isBlocked
        });
        
        // Update local state
        setAvailabilityDates(prev => 
          prev.map(avail => 
            datesEqual(avail.date, date)
              ? { ...avail, isBlocked: !avail.isBlocked }
              : avail
          )
        );
      } else {
        // Create new availability
        const newAvailability = createEmptyAvailabilityDate(date);
        newAvailability.isBlocked = true;
        
        await updateAvailabilityDate(date, newAvailability);
        
        // Add to local state
        setAvailabilityDates(prev => [...prev, newAvailability]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error toggling blocked status:', err);
      setError('Failed to update blocked status. Please try again.');
      setLoading(false);
    }
  };

  // Bulk update selected dates
  const handleBulkUpdate = async (updates: Partial<AvailabilityDate>) => {
    if (selectedDates.length === 0) return;
    
    try {
      setLoading(true);
      
      await bulkUpdateAvailability(selectedDates, updates);
      
      // Update local state
      setAvailabilityDates(prev => 
        prev.map(avail => {
          const isSelected = selectedDates.some(date => 
            datesEqual(date, avail.date)
          );
          
          if (isSelected) {
            return { ...avail, ...updates };
          }
          
          return avail;
        })
      );
      
      // Add new availability records for dates that don't exist
      const newDates = selectedDates.filter(date => 
        !availabilityDates.some(avail => 
          datesEqual(avail.date, date)
        )
      );
      
      if (newDates.length > 0) {
        const newAvailabilityRecords = newDates.map(date => ({
          ...createEmptyAvailabilityDate(date),
          ...updates
        }));
        
        setAvailabilityDates(prev => [...prev, ...newAvailabilityRecords]);
      }
      
      setSelectedDates([]);
      setShowBulkEdit(false);
      setLoading(false);
    } catch (err) {
      console.error('Error bulk updating availability:', err);
      setError('Failed to update availability. Please try again.');
      setLoading(false);
    }
  };

  // Update settings
  const handleUpdateSettings = async (newSettings: Partial<AvailabilitySettings>) => {
    if (!settings) return;
    
    try {
      setLoading(true);
      
      await updateAvailabilitySettings(newSettings);
      
      setSettings({
        ...settings,
        ...newSettings
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to update settings. Please try again.');
      setLoading(false);
    }
  };

  // Clear error
  const clearError = () => setError(null);

  // Handle day of week selection
  const handleDayOfWeekToggle = (dayOfWeek: number) => {
    setSelectedDaysOfWeek(prev => 
      prev.includes(dayOfWeek) 
        ? prev.filter(day => day !== dayOfWeek)
        : [...prev, dayOfWeek]
    );
  };

  // Get day name from day number
  const getDayName = (dayOfWeek: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  // Get availability for a specific date
  const getAvailabilityForDate = (date: Date): AvailabilityDate | undefined => {
    return availabilityDates.find(avail => 
      datesEqual(avail.date, date)
    );
  };

  // Check if a date is selected
  const isDateSelected = (date: Date): boolean => {
    return selectedDates.some(selectedDate => 
      datesEqual(selectedDate, date)
    );
  };

  // Toggle date selection
  const toggleDateSelection = (date: Date) => {
    if (isDateSelected(date)) {
      setSelectedDates(prev => 
        prev.filter(selectedDate => 
          selectedDate.toDateString() !== date.toDateString()
        )
      );
    } else {
      setSelectedDates(prev => [...prev, date]);
    }
  };

  if (loading && !settings) {
    return (
      <div className="availability-page availability-page--loading">
        <LogoSpinner size="large" text="Loading availability data..." />
      </div>
    );
  }

  return (
    <div className={`availability-page ${className}`}>
      {/* Error Message */}
      {error && (
        <div className="availability-page__error">
          <span>{error}</span>
          <button onClick={clearError} className="error-dismiss">
            <span>×</span>
          </button>
        </div>
      )}

      {/* Page Header */}
      <header className="availability-page__header">
        <div className="availability-page__title">
          <h1>Availability Dashboard</h1>
          <p>Manage your collection and delivery calendar</p>
        </div>
        
        <div className="availability-page__actions">
          <Button
            variant="secondary"
            size="medium"
            onClick={forceRefresh}
            disabled={loading}
            className="action-refresh"
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>
      </header>

      {/* Dashboard Layout */}
      <div className="availability-dashboard-layout">
        {/* Main Content - Calendar */}
        <div className="availability-main-content">
          <div className="availability-dashboard-card">
            <div className="card-header">
              <h3>Availability Calendar</h3>
              <p>Click dates to block/unblock. Selected dates appear below for bulk editing.</p>
            </div>
            <div className="availability-page__calendar">
              <Calendar
                selectedDate={selectedDate || undefined}
                onDateSelect={handleDateSelect}
                onDateClick={handleDateClick}
                availabilityDates={availabilityDates}
                availabilitySettings={settings}
                currentMonth={currentMonth}
                onMonthChange={handleMonthChange}
                className="availability-calendar"
                adminMode
              />
            </div>
          </div>

          {/* Bulk Edit Panel */}
          {showBulkEdit && selectedDates.length > 0 && (
            <div className="availability-dashboard-card availability-page__bulk-edit">
              <div className="card-header">
                <h3>Bulk Edit Selected Dates ({selectedDates.length})</h3>
              </div>
              <div className="bulk-edit-actions">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => handleBulkUpdate({ isAvailable: true, isBlocked: false })}
                  disabled={loading}
                >
                  Make Available
                </Button>
                
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => handleBulkUpdate({ isAvailable: false })}
                  disabled={loading}
                >
                  Make Unavailable
                </Button>
                
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => handleBulkUpdate({ isBlocked: true })}
                  disabled={loading}
                >
                  Block Dates
                </Button>
                
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setSelectedDates([])}
                  disabled={loading}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Content */}
        <div className="availability-side-content">
          {/* Quick Actions */}
          <div className="availability-dashboard-card">
            <div className="card-header">
              <h3>Quick Actions</h3>
            </div>
            
            <div className="quick-actions-grid">
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  const today = new Date();
                  toggleDateAvailability(today);
                }}
                disabled={loading}
              >
                Block/Unblock Today
              </Button>
              
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  toggleDateAvailability(tomorrow);
                }}
                disabled={loading}
              >
                Block/Unblock Tomorrow
              </Button>
              
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  const nextWeek = new Date();
                  nextWeek.setDate(nextWeek.getDate() + 7);
                  toggleDateAvailability(nextWeek);
                }}
                disabled={loading}
              >
                Block/Unblock Next Week
              </Button>
            </div>

            <div className="quick-actions-advanced">
              <div className="card-header">
                <h4>Advanced Actions</h4>
              </div>
              
              <div className="quick-action-group">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => toggleAllDatesInMonth(true)}
                  disabled={loading}
                >
                  Block All This Month
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => toggleAllDatesInMonth(false)}
                  disabled={loading}
                >
                  Unblock All This Month
                </Button>
              </div>

              <div className="quick-action-group">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setShowDayOfWeekSelector(!showDayOfWeekSelector)}
                  disabled={loading}
                >
                  Manage Days of Week
                </Button>
                
                {showDayOfWeekSelector && (
                  <div className="day-of-week-selector">
                    <h5>Select days to manage (next 6 months):</h5>
                    <div className="day-checkboxes">
                      {[0, 1, 2, 3, 4, 5, 6].map(dayOfWeek => (
                        <label key={dayOfWeek} className="day-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedDaysOfWeek.includes(dayOfWeek)}
                            onChange={() => handleDayOfWeekToggle(dayOfWeek)}
                          />
                          {getDayName(dayOfWeek)}
                        </label>
                      ))}
                    </div>
                    <div className="day-of-week-actions">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => toggleDaysOfWeek(true)}
                        disabled={loading || selectedDaysOfWeek.length === 0}
                      >
                        Block Days
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => toggleDaysOfWeek(false)}
                        disabled={loading || selectedDaysOfWeek.length === 0}
                      >
                        Unblock Days
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="quick-action-group">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setShowDateRangeSelector(!showDateRangeSelector)}
                  disabled={loading}
                >
                  Manage Date Range
                </Button>
                
                {showDateRangeSelector && (
                  <div className="date-range-selector">
                    <h5>Select date range:</h5>
                    <div className="date-range-inputs">
                      <div className="date-input-group">
                        <label>From:</label>
                        <input
                          type="date"
                          value={dateRangeFrom}
                          onChange={(e) => setDateRangeFrom(e.target.value)}
                        />
                      </div>
                      <div className="date-input-group">
                        <label>To:</label>
                        <input
                          type="date"
                          value={dateRangeTo}
                          onChange={(e) => setDateRangeTo(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="date-range-actions">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => toggleDateRange(true)}
                        disabled={loading || !dateRangeFrom || !dateRangeTo}
                      >
                        Block Range
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => toggleDateRange(false)}
                        disabled={loading || !dateRangeFrom || !dateRangeTo}
                      >
                        Unblock Range
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Global Settings */}
          {settings && (
            <div className="availability-dashboard-card availability-page__settings">
              <div className="card-header">
                <h3>Global Rules</h3>
              </div>
              <div className="settings-grid">
                <div className="setting-item">
                  <label htmlFor="weekday-max-bookings">Max bookings on weekdays (Mon–Fri)</label>
                  <input
                    id="weekday-max-bookings"
                    type="number"
                    min={0}
                    value={settings.weekdayMaxBookings}
                    onChange={(e) => handleUpdateSettings({
                      weekdayMaxBookings: parseInt(e.target.value) || 0
                    })}
                    disabled={loading}
                  />
                </div>

                <div className="setting-item">
                  <label htmlFor="weekend-max-bookings">Max bookings on weekends (Sat–Sun)</label>
                  <input
                    id="weekend-max-bookings"
                    type="number"
                    min={0}
                    value={settings.weekendMaxBookings}
                    onChange={(e) => handleUpdateSettings({
                      weekendMaxBookings: parseInt(e.target.value) || 0
                    })}
                    disabled={loading}
                  />
                </div>
                
                <div className="setting-item">
                  <label>Advance Booking Days</label>
                  <input
                    type="number"
                    value={settings.advanceBookingDays}
                    onChange={(e) => handleUpdateSettings({
                      advanceBookingDays: parseInt(e.target.value) || 0
                    })}
                    disabled={loading}
                  />
                </div>
                
                <div className="setting-item">
                  <label>Working Hours Start</label>
                  <input
                    type="time"
                    value={settings.workingHours.start}
                    onChange={(e) => handleUpdateSettings({
                      workingHours: { ...settings.workingHours, start: e.target.value }
                    })}
                    disabled={loading}
                  />
                </div>
                
                <div className="setting-item">
                  <label>Working Hours End</label>
                  <input
                    type="time"
                    value={settings.workingHours.end}
                    onChange={(e) => handleUpdateSettings({
                      workingHours: { ...settings.workingHours, end: e.target.value }
                    })}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="availability-page__loading-overlay">
          <LogoSpinner size="medium" text="Updating..." />
        </div>
      )}
    </div>
  );
};

export default AvailabilityPage;
